import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, BookOpen, Lightbulb, Target, FileText, Users, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';

const tutorialSteps = [
  {
    title: "Welcome to SpEdKit!",
    description: "SpEdKit is your complete special education toolkit. Let's take a quick tour of what you can do.",
    icon: BookOpen,
    color: "bg-primary",
    features: []
  },
  {
    title: "Create Social Stories",
    description: "Generate personalized social stories with AI illustrations. Perfect for teaching social-emotional skills to students.",
    icon: BookOpen,
    color: "bg-primary",
    features: [
      "3-step wizard guides you through creation",
      "AI generates text and images based on your input",
      "Text-to-speech reads stories aloud",
      "Download as PDF to share with families"
    ]
  },
  {
    title: "Find UDL Strategies",
    description: "The UDL Strategy Hunter helps you find evidence-based accommodations and Universal Design for Learning strategies.",
    icon: Lightbulb,
    color: "bg-accent",
    features: [
      "Describe a learning barrier",
      "Get practical strategies organized by UDL principle",
      "Each strategy includes implementation steps",
      "Technology recommendations included"
    ]
  },
  {
    title: "Generate IEP Goals",
    description: "Create SMART goals aligned to academic and SEL standards. Start from a story or select an anchor standard.",
    icon: Target,
    color: "bg-indigo-500",
    features: [
      "Goals from social stories or standards",
      "CASEL, CCSS, NGSS, and PKTLF alignment",
      "Option to include short-term objectives",
      "Progress monitoring recommendations"
    ]
  },
  {
    title: "Write Present Levels",
    description: "Generate comprehensive PLAAFP statements for IEP documentation based on your observations and assessment data.",
    icon: FileText,
    color: "bg-pink-500",
    features: [
      "Select multiple areas of need",
      "Enter observations and assessment data",
      "AI generates complete PLAAFP sections",
      "Download as PDF for IEP meetings"
    ]
  },
  {
    title: "Organize & Track",
    description: "Keep everything organized by student and track progress over time with our classroom tools.",
    icon: Users,
    color: "bg-purple-500",
    features: [
      "Create student folders",
      "Track skill progress with data entry",
      "View progress graphs",
      "Email reports to families"
    ]
  }
];

const QuickStartTutorial = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="quick-start-tutorial">
      <div className="bg-white rounded-2xl shadow-float max-w-lg w-full overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div className={`${step.color} p-6 text-white relative`}>
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close tutorial"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <step.icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/70 text-sm">Step {currentStep + 1} of {tutorialSteps.length}</p>
              <h2 className="font-heading font-bold text-xl">{step.title}</h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="font-body text-text-secondary mb-4">{step.description}</p>
          
          {step.features.length > 0 && (
            <ul className="space-y-2 mb-6">
              {step.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-text-primary text-sm">
                  <div className={`w-5 h-5 ${step.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <ChevronRight className="w-3 h-3 text-white" />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>
          )}

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {tutorialSteps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep ? `${step.color} w-4` : 'bg-secondary'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`text-text-secondary ${isFirstStep ? 'invisible' : ''}`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-text-muted"
              >
                Skip Tour
              </Button>
              <Button
                onClick={handleNext}
                className={`${step.color} text-white hover:opacity-90`}
              >
                {isLastStep ? 'Get Started' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStartTutorial;
